<template>
  <div class="relative">
    <!-- Left gradient -->
    <div
      v-if="showScrollLeft"
      class="absolute left-0 top-0 bottom-0 w-12 z-10 pointer-events-none"
      :style="{
        background: `linear-gradient(to right, ${gradientBgVar}, transparent)`,
      }"
    ></div>

    <!-- Scrollable tabs container -->
    <div ref="scrollContainer" class="flex overflow-x-auto no-scrollbar" @scroll="handleScroll">
      <div
        v-for="(tab, index) in tabs"
        :key="index"
        :ref="(el) => setTabRef(el, index)"
        class="text-strongest flex items-center flex-shrink-0"
      >
        <div
          class="cursor-pointer px-3 h-10 flex items-center rounded-full text-sm whitespace-nowrap"
          :class="index === activeIndex ? 'bg-tab-bg-selected' : 'bg-transparent'"
          @click="$emit('tab-click', index)"
        >
          <div class="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <div
              v-if="index < activeIndex || isReview || isModalEdit"
              class="h-4 w-4 rounded-full bg-success flex items-center justify-center"
            >
              <Icon name="Check" class="h-2.5 w-2.5 text-strongest-inverse" :stroke-width="3" />
            </div>
            <Icon
              v-else-if="index === activeIndex"
              name="Circle"
              class="h-4 w-4 text-filter-outline-selected-chip-icon"
            />
            <Icon v-else name="Circle" class="h-4 w-4 text-outline-dividers" />
          </div>
          <p
            class="ml-2.5 font-inter"
            :class="index === activeIndex ? 'font-semibold' : 'font-normal'"
          >
            <span class="invisible font-semibold h-0 block font-inter">{{ tab.name }}</span>
            <span class="font-inter">{{ tab.name }}</span>
          </p>
        </div>
        <div
          v-if="index < tabs.length - 1"
          class="w-[14px] h-0.5 mx-3 bg-outline-dividers flex-shrink-0"
        ></div>
      </div>
    </div>

    <!-- Right gradient -->
    <div
      v-if="showScrollRight"
      class="absolute right-0 top-0 bottom-0 w-12 z-10 pointer-events-none"
      :style="{
        background: `linear-gradient(to left, ${gradientBgVar}, transparent)`,
      }"
    ></div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onBeforeUpdate, computed } from "vue";
import { Icon } from "@/components";
import { useGradientScroll } from "@/composables/useGradientScroll";

const props = defineProps({
  tabs: {
    type: Array,
    default: () => [],
  },
  activeIndex: {
    type: Number,
    default: 0,
  },
  isReview: {
    type: Boolean,
    default: false,
  },
  isModalEdit: {
    type: Boolean,
    default: false,
  },
});

defineEmits(["tab-click"]);

// Use the gradient scroll composable
const { scrollContainer, showScrollRight, showScrollLeft, handleScroll } = useGradientScroll();

const tabRefs = ref([]);

const gradientBgVar = computed(() => (props.isReview ? "var(--container-bg)" : "var(--bg-canvas)"));

// Clear refs before each update
onBeforeUpdate(() => {
  tabRefs.value = [];
});

const setTabRef = (el, index) => {
  if (el) {
    tabRefs.value[index] = el;
  }
};

// Watch for active index changes and scroll to the active tab
watch(
  () => props.activeIndex,
  async (newIndex) => {
    await nextTick();

    const activeTab = tabRefs.value[newIndex];
    if (activeTab && scrollContainer.value) {
      const containerWidth = scrollContainer.value.clientWidth;
      const tabLeft = activeTab.offsetLeft;
      const tabWidth = activeTab.offsetWidth;

      // Calculate scroll position to center the tab
      const scrollLeftPosition = tabLeft - containerWidth / 2 + tabWidth / 2;

      scrollContainer.value.scrollTo({
        left: scrollLeftPosition,
        behavior: "smooth",
      });
    }
  },
  { immediate: true }
);
</script>
